import React, { useState, useEffect, useCallback } from 'react';
import { _T, C, CSS } from '../../shared/theme';

const RIE_QUESTIONS = [
  {
    id: 'workload',
    nl: { label: 'Werkdruk', question: 'Hoe ervaar je de werkdruk in jouw functie?', options: ['Laag', 'Acceptabel', 'Hoog', 'Zeer hoog'] },
    en: { label: 'Workload', question: 'How do you experience the workload in your role?', options: ['Low', 'Acceptable', 'High', 'Very high'] },
  },
  {
    id: 'autonomy',
    nl: { label: 'Autonomie', question: 'In hoeverre heb je zeggenschap over je eigen werkwijze?', options: ['Nauwelijks', 'Weinig', 'Redelijk', 'Veel'] },
    en: { label: 'Autonomy', question: 'To what extent do you have control over how you work?', options: ['Hardly', 'Little', 'Moderate', 'A lot'] },
  },
  {
    id: 'support',
    nl: { label: 'Sociale steun', question: 'Hoe zou je de steun van collega\'s en leidinggevende omschrijven?', options: ['Onvoldoende', 'Matig', 'Goed', 'Uitstekend'] },
    en: { label: 'Social support', question: 'How would you describe support from colleagues and your manager?', options: ['Insufficient', 'Moderate', 'Good', 'Excellent'] },
  },
  {
    id: 'harassment',
    nl: { label: 'Ongewenst gedrag', question: 'Heb je te maken gehad met pesten, agressie of discriminatie?', options: ['Regelmatig', 'Soms', 'Zelden', 'Nooit'] },
    en: { label: 'Unwanted behaviour', question: 'Have you experienced bullying, aggression or discrimination?', options: ['Regularly', 'Sometimes', 'Rarely', 'Never'] },
  },
  {
    id: 'balance',
    nl: { label: 'Werk-privébalans', question: 'Hoe beoordeel je de balans tussen werk en privéleven?', options: ['Slecht', 'Matig', 'Goed', 'Uitstekend'] },
    en: { label: 'Work-life balance', question: 'How do you rate the balance between work and private life?', options: ['Poor', 'Moderate', 'Good', 'Excellent'] },
  },
  {
    id: 'clarity',
    nl: { label: 'Rolhelderheid', question: 'Is het duidelijk wat er van jou verwacht wordt in je rol?', options: ['Nooit', 'Soms', 'Meestal', 'Altijd'] },
    en: { label: 'Role clarity', question: 'Is it clear what is expected of you in your role?', options: ['Never', 'Sometimes', 'Usually', 'Always'] },
  },
  {
    id: 'growth',
    nl: { label: 'Ontwikkelmogelijkheden', question: 'Zijn er voldoende mogelijkheden voor professionele groei?', options: ['Nauwelijks', 'Weinig', 'Voldoende', 'Veel'] },
    en: { label: 'Growth opportunities', question: 'Are there sufficient opportunities for professional development?', options: ['Hardly', 'Little', 'Sufficient', 'Plenty'] },
  },
  {
    id: 'burnout_risk',
    nl: { label: 'Burn-out signalen', question: 'Voel je je regelmatig uitgeput na een werkdag?', options: ['Altijd', 'Vaak', 'Soms', 'Zelden'] },
    en: { label: 'Burnout signals', question: 'Do you regularly feel exhausted after a working day?', options: ['Always', 'Often', 'Sometimes', 'Rarely'] },
  },
];

const RISK_COLORS = { low: '#4AC87A', medium: '#C8A84B', high: '#C05050' };

function calcRisk(answers) {
  if (!answers || Object.keys(answers).length === 0) return 'unknown';
  const scores = RIE_QUESTIONS.map(q => {
    const opts = q.nl.options; // risk order is the same regardless of lang
    const ans  = answers[q.id];
    const idx  = opts.indexOf(ans);
    return idx >= 0 ? idx : 1;
  });
  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  if (avg < 1.5) return 'high';
  if (avg < 2.2) return 'medium';
  return 'low';
}

export default function BusinessRIEScreen({ lang, businessId, currentUserId, role }) {
  const nl = lang === 'nl';
  const [tab, setTab] = useState('rie');
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [results, setResults] = useState([]);
  const [myAnswers, setMyAnswers] = useState({});
  const [answering, setAnswering] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);

  const [enpsData, setEnpsData] = useState([]);
  const [myScore, setMyScore] = useState(null);
  const [myComment, setMyComment] = useState('');
  const [enpsSubmitted, setEnpsSubmitted] = useState(false);

  const BASE   = process.env.REACT_APP_API_URL || '/api';
  const isAdmin = role === 'owner' || role === 'admin';

  const loadRIE = useCallback(() => {
    fetch(`${BASE}/businesses/${businessId}/rie`).then(r => r.json()).then(setSessions).catch(() => {});
  }, [BASE, businessId]);

  const loadEnps = useCallback(() => {
    fetch(`${BASE}/businesses/${businessId}/enps`).then(r => r.json()).then(data => {
      if (Array.isArray(data)) setEnpsData(data);
    }).catch(() => {});
  }, [BASE, businessId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${BASE}/businesses/${businessId}/rie`).then(r => r.json()),
      fetch(`${BASE}/businesses/${businessId}/enps`).then(r => r.json()),
    ]).then(([rieData, enpsD]) => {
      if (Array.isArray(rieData)) setSessions(rieData);
      if (Array.isArray(enpsD)) setEnpsData(enpsD);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [BASE, businessId]);

  const createSession = () => {
    const quarter = Math.ceil((new Date().getMonth() + 1) / 3);
    const title = prompt(nl ? 'Naam van de assessment:' : 'Assessment title:', `Q${quarter} ${new Date().getFullYear()} RI&E`);
    if (!title) return;
    fetch(`${BASE}/businesses/${businessId}/rie`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ created_by: currentUserId, title }),
    }).then(() => loadRIE()).catch(() => {});
  };

  const closeSession = (id) => {
    if (!window.confirm(nl ? 'Assessment sluiten? Er worden geen nieuwe antwoorden meer geaccepteerd.' : 'Close this assessment? No more responses will be accepted.')) return;
    fetch(`${BASE}/businesses/${businessId}/rie/${id}`, { method: 'PUT' }).then(() => loadRIE()).catch(() => {});
  };

  const openResults = (sess) => {
    setActiveSession(sess);
    fetch(`${BASE}/businesses/${businessId}/rie/${sess.id}/results`).then(r => r.json()).then(setResults).catch(() => setResults([]));
  };

  const startAnswering = (sess) => {
    setActiveSession(sess);
    setAnswering(true);
    setMyAnswers({});
    setSubmitted(false);
  };

  const submitAnswers = () => {
    fetch(`${BASE}/businesses/${businessId}/rie/${activeSession.id}/answer`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: currentUserId, answers: myAnswers }),
    }).then(() => {
      setSubmitted(true);
      setTimeout(() => { setAnswering(false); setActiveSession(null); loadRIE(); }, 2000);
    }).catch(() => {});
  };

  const submitEnps = () => {
    if (myScore === null) return;
    fetch(`${BASE}/businesses/${businessId}/enps`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: currentUserId, score: myScore, comment: myComment }),
    }).then(() => { setEnpsSubmitted(true); loadEnps(); }).catch(() => {});
  };

  const enpsColor = (score) => score >= 9 ? '#4AC87A' : score >= 7 ? '#C8A84B' : '#C05050';

  if (loading) return <div style={{ padding: 24, textAlign: 'center', color: C.silver, fontSize: 13 }}>...</div>;

  // ── Answer view ──────────────────────────────────────────────────────────────
  if (answering && activeSession) {
    const allAnswered = RIE_QUESTIONS.every(q => myAnswers[q.id]);
    return (
      <div style={{ padding: '0 0 32px' }}>
        <style>{CSS}</style>
        <button onClick={() => { setAnswering(false); setActiveSession(null); }}
          style={{ background: 'none', border: 'none', color: _T.accent, fontSize: 14, cursor: 'pointer', padding: '0 0 12px' }}>
          ← {nl ? 'Terug' : 'Back'}
        </button>
        <div style={{ color: C.white, fontSize: 17, fontWeight: 700, marginBottom: 4 }}>{activeSession.title}</div>
        <div style={{ color: C.silver, fontSize: 12, marginBottom: 20 }}>
          {nl ? 'Anoniem — jouw antwoorden zijn niet gekoppeld aan jouw naam' : 'Anonymous — your answers are not linked to your name'}
        </div>

        {submitted ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#4AC87A', fontSize: 15 }}>
            ✓ {nl ? 'Bedankt! Jouw antwoorden zijn anoniem ingediend.' : 'Thank you! Your responses have been submitted anonymously.'}
          </div>
        ) : (
          <>
            {RIE_QUESTIONS.map((q) => {
              const lq = nl ? q.nl : q.en;
              return (
                <div key={q.id} style={{ background: _T.bg3, borderRadius: 14, padding: 16, marginBottom: 12, border: `1px solid ${_T.cardBorderBot}` }}>
                  <div style={{ fontSize: 10, color: _T.accent, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{lq.label}</div>
                  <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, color: C.white }}>{lq.question}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {lq.options.map((opt) => (
                      <button key={opt} onClick={() => setMyAnswers(p => ({ ...p, [q.id]: opt }))}
                        style={{ padding: '10px 8px', borderRadius: 10, border: `2px solid ${myAnswers[q.id] === opt ? _T.accent : _T.cardBorderBot}`, background: myAnswers[q.id] === opt ? `${_T.accent}18` : _T.bg4, fontSize: 13, cursor: 'pointer', fontWeight: myAnswers[q.id] === opt ? 700 : 400, color: myAnswers[q.id] === opt ? _T.accent : C.silver, transition: 'all 0.15s' }}>
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
            <button onClick={submitAnswers} disabled={!allAnswered}
              style={{ width: '100%', padding: 14, background: allAnswered ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : _T.bg4, color: allAnswered ? '#0a1220' : C.silver2, border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: allAnswered ? 'pointer' : 'not-allowed', marginTop: 8 }}>
              {nl ? 'Assessment indienen' : 'Submit Assessment'}
            </button>
          </>
        )}
      </div>
    );
  }

  // ── Results view ─────────────────────────────────────────────────────────────
  if (activeSession && !answering) {
    const riskCounts = { low: 0, medium: 0, high: 0, unknown: 0 };
    results.forEach(r => { const risk = calcRisk(r.answers); riskCounts[risk]++; });
    return (
      <div style={{ padding: '0 0 32px' }}>
        <style>{CSS}</style>
        <button onClick={() => setActiveSession(null)}
          style={{ background: 'none', border: 'none', color: _T.accent, fontSize: 14, cursor: 'pointer', padding: '0 0 12px' }}>
          ← {nl ? 'Terug' : 'Back'}
        </button>
        <div style={{ color: C.white, fontSize: 17, fontWeight: 700, marginBottom: 4 }}>
          {activeSession.title} — {nl ? 'Resultaten' : 'Results'}
        </div>
        <div style={{ color: C.silver, fontSize: 12, marginBottom: 20 }}>
          {results.length} {nl ? 'respons(en)' : `response${results.length !== 1 ? 's' : ''}`}
        </div>

        {results.length === 0 ? (
          <div style={{ textAlign: 'center', color: C.silver, padding: 32 }}>{nl ? 'Nog geen antwoorden' : 'No responses yet'}</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
              {[['high', nl ? 'Hoog risico' : 'High Risk'], ['medium', nl ? 'Gemiddeld' : 'Medium Risk'], ['low', nl ? 'Laag risico' : 'Low Risk']].map(([level, label]) => (
                <div key={level} style={{ background: _T.bg3, borderRadius: 12, padding: 14, border: `2px solid ${RISK_COLORS[level]}`, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: RISK_COLORS[level] }}>{riskCounts[level]}</div>
                  <div style={{ fontSize: 10, color: C.silver, marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>

            {RIE_QUESTIONS.map(q => {
              const lq = nl ? q.nl : q.en;
              const counts = {};
              lq.options.forEach(o => { counts[o] = 0; });
              results.forEach(r => {
                // answers may be in Dutch or English; match by index
                const allOpts = [q.nl.options, q.en.options];
                let idx = -1;
                for (const opts of allOpts) {
                  const i = opts.indexOf(r.answers?.[q.id]);
                  if (i >= 0) { idx = i; break; }
                }
                if (idx >= 0 && lq.options[idx]) counts[lq.options[idx]]++;
              });
              const total = results.length;
              return (
                <div key={q.id} style={{ background: _T.bg3, borderRadius: 12, padding: 14, marginBottom: 10, border: `1px solid ${_T.cardBorderBot}` }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: C.white }}>{lq.label}: {lq.question}</div>
                  {lq.options.map((opt, oi) => {
                    const pct    = total > 0 ? Math.round((counts[opt] || 0) / total * 100) : 0;
                    const isRisk = oi === 0;
                    return (
                      <div key={opt} style={{ marginBottom: 6 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                          <span style={{ color: isRisk && pct > 20 ? '#C05050' : C.silver }}>{opt}</span>
                          <span style={{ color: C.silver2 }}>{counts[opt] || 0} ({pct}%)</span>
                        </div>
                        <div style={{ background: _T.bg4, borderRadius: 4, height: 6 }}>
                          <div style={{ width: `${pct}%`, height: '100%', borderRadius: 4, background: isRisk && pct > 20 ? '#C05050' : _T.accent, transition: 'width 0.4s' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </>
        )}
      </div>
    );
  }

  // ── Main view ────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '0 0 32px' }}>
      <style>{CSS}</style>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[['rie', 'RI&E Assessment'], ['enps', 'eNPS Tracking']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: `1.5px solid ${tab === key ? _T.accent : _T.cardBorderBot}`, background: tab === key ? `${_T.accent}18` : _T.bg3, color: tab === key ? _T.accent : C.silver, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {label}
          </button>
        ))}
      </div>

      {/* RI&E Tab */}
      {tab === 'rie' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <div style={{ color: C.white, fontSize: 15, fontWeight: 700 }}>
                {nl ? 'Psychosociaal risico-onderzoek' : 'Psychosocial Risk Assessment'}
              </div>
              <div style={{ color: C.silver, fontSize: 11, marginTop: 2 }}>
                {nl ? 'Verplicht onder de Nederlandse Arbowet' : 'Required by Dutch Arbowet'}
              </div>
            </div>
            {isAdmin && (
              <button onClick={createSession}
                style={{ background: `${_T.accent}20`, border: `1px solid ${_T.accent}55`, borderRadius: 10, color: _T.accent, fontSize: 13, fontWeight: 600, padding: '8px 14px', cursor: 'pointer' }}>
                + {nl ? 'Nieuw' : 'New'}
              </button>
            )}
          </div>

          {sessions.length === 0 ? (
            <div style={{ textAlign: 'center', color: C.silver, padding: 40 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
              <div>{nl ? 'Nog geen assessments' : 'No assessments yet'}</div>
              {isAdmin && <div style={{ fontSize: 12, marginTop: 4, color: C.silver2 }}>{nl ? 'Maak er een aan om anonieme antwoorden te verzamelen' : 'Create one to start collecting anonymous responses'}</div>}
            </div>
          ) : (
            sessions.map(sess => {
              const isOpen = sess.status === 'open';
              return (
                <div key={sess.id} style={{ background: _T.bg3, borderRadius: 14, padding: 16, marginBottom: 10, border: `1px solid ${_T.cardBorderBot}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.white }}>{sess.title}</div>
                      <div style={{ fontSize: 12, color: C.silver, marginTop: 2 }}>
                        {new Date(sess.created_at).toLocaleDateString('nl-NL')} · {sess.response_count} {nl ? 'antwoorden' : 'responses'}
                      </div>
                    </div>
                    <span style={{ background: isOpen ? `#4AC87A20` : _T.bg4, color: isOpen ? '#4AC87A' : C.silver2, padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, border: `1px solid ${isOpen ? '#4AC87A44' : _T.cardBorderBot}` }}>
                      {isOpen ? (nl ? 'Open' : 'Open') : (nl ? 'Gesloten' : 'Closed')}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    {isOpen && (
                      <button onClick={() => startAnswering(sess)}
                        style={{ flex: 1, padding: '9px 0', background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, color: '#0a1220', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        {nl ? 'Invullen' : 'Fill in'}
                      </button>
                    )}
                    {isAdmin && (
                      <button onClick={() => openResults(sess)}
                        style={{ flex: 1, padding: '9px 0', background: _T.bg4, color: C.silver, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, fontSize: 13, cursor: 'pointer' }}>
                        {nl ? 'Resultaten' : 'View Results'}
                      </button>
                    )}
                    {isAdmin && isOpen && (
                      <button onClick={() => closeSession(sess.id)}
                        style={{ padding: '9px 12px', background: `#C0505015`, color: '#C05050', border: `1px solid #C0505055`, borderRadius: 10, fontSize: 12, cursor: 'pointer' }}>
                        {nl ? 'Sluiten' : 'Close'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </>
      )}

      {/* eNPS Tab */}
      {tab === 'enps' && (
        <>
          <div style={{ color: C.white, fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
            {nl ? 'Medewerkers NPS' : 'Employee Net Promoter Score'}
          </div>
          <div style={{ color: C.silver, fontSize: 12, marginBottom: 16 }}>
            {nl ? 'Maandelijks: "Hoe waarschijnlijk is het dat je dit bedrijf aanbeveelt?"' : 'Monthly: "How likely are you to recommend this company?"'}
          </div>

          {!enpsSubmitted ? (
            <div style={{ background: _T.bg3, borderRadius: 14, padding: 16, marginBottom: 20, border: `1px solid ${_T.cardBorderBot}` }}>
              <div style={{ color: C.white, fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                {nl ? 'Enquête van deze maand' : "This month's survey"}
              </div>
              <div style={{ color: C.silver, fontSize: 13, marginBottom: 12 }}>
                {nl ? 'Hoe waarschijnlijk is het dat je dit bedrijf aanbeveelt als geweldige werkplek? (0–10)' : 'How likely are you to recommend this company as a great place to work? (0–10)'}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                {Array.from({ length: 11 }, (_, i) => (
                  <button key={i} onClick={() => setMyScore(i)}
                    style={{ width: 34, height: 34, borderRadius: 8, border: `2px solid ${myScore === i ? _T.accent : _T.cardBorderBot}`, background: myScore === i ? `${_T.accent}20` : _T.bg4, fontSize: 13, fontWeight: myScore === i ? 700 : 400, color: myScore === i ? _T.accent : C.silver, cursor: 'pointer' }}>
                    {i}
                  </button>
                ))}
              </div>
              {myScore !== null && (
                <div style={{ marginBottom: 8, fontSize: 12, color: enpsColor(myScore), fontWeight: 600 }}>
                  {myScore >= 9 ? (nl ? 'Promotor' : 'Promoter') : myScore >= 7 ? (nl ? 'Passief' : 'Passive') : (nl ? 'Tegenstander' : 'Detractor')}
                </div>
              )}
              <textarea placeholder={nl ? 'Optionele opmerking…' : 'Optional comment…'} value={myComment}
                onChange={e => setMyComment(e.target.value)}
                style={{ width: '100%', padding: 10, borderRadius: 10, border: `1px solid ${_T.cardBorderBot}`, background: _T.bg4, color: C.white, fontSize: 13, resize: 'vertical', minHeight: 60, boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: 10 }} />
              <button onClick={submitEnps} disabled={myScore === null}
                style={{ width: '100%', padding: 12, background: myScore !== null ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : _T.bg4, color: myScore !== null ? '#0a1220' : C.silver2, border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: myScore !== null ? 'pointer' : 'not-allowed' }}>
                {nl ? 'Indienen' : 'Submit'}
              </button>
            </div>
          ) : (
            <div style={{ background: `#4AC87A15`, borderRadius: 14, padding: 16, marginBottom: 20, textAlign: 'center', color: '#4AC87A', border: `1px solid #4AC87A44` }}>
              ✓ {nl ? 'Bedankt voor je respons deze maand!' : 'Thanks for your response this month!'}
            </div>
          )}

          {isAdmin && enpsData.length > 0 && (
            <div style={{ background: _T.bg3, borderRadius: 14, padding: 16, border: `1px solid ${_T.cardBorderBot}` }}>
              <div style={{ color: C.white, fontSize: 14, fontWeight: 600, marginBottom: 14 }}>
                {nl ? 'eNPS Trend (laatste 6 maanden)' : 'eNPS Trend (Last 6 Months)'}
              </div>
              {enpsData.map(m => {
                const enps  = m.enps ?? 0;
                const color = enps >= 20 ? '#4AC87A' : enps >= 0 ? '#C8A84B' : '#C05050';
                return (
                  <div key={m.month} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                      <span style={{ color: C.silver }}>{m.month}</span>
                      <span style={{ fontWeight: 700, color }}>{enps > 0 ? '+' : ''}{enps}</span>
                    </div>
                    <div style={{ background: _T.bg4, borderRadius: 6, height: 8, position: 'relative' }}>
                      <div style={{ position: 'absolute', left: '50%', top: 0, width: 1, height: '100%', background: _T.cardBorderBot }} />
                      {enps >= 0
                        ? <div style={{ position: 'absolute', left: '50%', width: `${Math.min(50, enps / 2)}%`, height: '100%', background: color, borderRadius: '0 6px 6px 0' }} />
                        : <div style={{ position: 'absolute', right: '50%', width: `${Math.min(50, Math.abs(enps) / 2)}%`, height: '100%', background: color, borderRadius: '6px 0 0 6px' }} />
                      }
                    </div>
                    <div style={{ fontSize: 11, color: C.silver2, marginTop: 2 }}>
                      {m.responses} {nl ? 'ant.' : 'resp.'} · {m.promoters} {nl ? 'promotors' : 'promoters'} · {m.detractors} {nl ? 'tegenstanders' : 'detractors'}
                    </div>
                  </div>
                );
              })}
              <div style={{ fontSize: 11, color: C.silver2, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${_T.cardBorderBot}` }}>
                eNPS = (Promotors − Tegenstanders) ÷ Totaal × 100 · Range: −100 tot +100
              </div>
            </div>
          )}

          {isAdmin && enpsData.length === 0 && (
            <div style={{ textAlign: 'center', color: C.silver, padding: 32 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📊</div>
              <div>{nl ? 'Nog geen eNPS-data' : 'No eNPS data yet'}</div>
              <div style={{ fontSize: 12, marginTop: 4, color: C.silver2 }}>{nl ? 'Antwoorden van medewerkers verschijnen hier' : "Members' responses will appear here"}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
